import { z } from "zod";

// ============================================================================
// CLUSTER LIST SCHEMAS
// ============================================================================

export const ClusterSummarySchema = z.object({
  cluster_id: z.string(),
  cluster_name: z.string(),
  workspace_id: z.string(),
  workspace_name: z.string().nullable().optional(),
  instance_id: z.number().nullable().optional(),
  driver_node_type: z.string().nullable().optional(),
  worker_node_type: z.string().nullable().optional(),
  worker_count: z.number().nullable().optional(),
  min_autoscale_workers: z.number().nullable().optional(),
  max_autoscale_workers: z.number().nullable().optional(),
  cluster_source: z.string().nullable().optional(),
  compute_type: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  last_state_change: z.string().nullable().optional(),
  enable_photon: z.boolean().nullable().optional(),
  avg_cpu: z.number().default(0),
  avg_memory: z.number().default(0),
  sample_count: z.number().default(0),
  total_cost: z.number().default(0),
  // Auto Tune enrichment — populated for all-purpose clusters only; jobs/DLT
  // rows have no Auto Tune surface and stay null.
  realized_savings: z.number().nullish(),
  estimated_annualized_savings: z.number().nullish(),
  is_auto_tune_enabled: z.enum(["auto-tune-enabled", "auto-tune-disabled"]).nullish(),
});

export const ClustersResponseSchema = z.object({
  clusters: z.array(ClusterSummarySchema),
  total_count: z.number(),
  page: z.number().default(1),
  page_size: z.number().default(20),
  total_pages: z.number().default(1),
});

export type ClusterSummary = z.infer<typeof ClusterSummarySchema>;
export type ClustersResponse = z.infer<typeof ClustersResponseSchema>;

// ============================================================================
// CLUSTER DETAIL SCHEMAS
// ============================================================================

export const ClusterDetailSchema = z.object({
  cluster_id: z.string(),
  cluster_name: z.string(),
  workspace_id: z.string(),
  workspace_name: z.string().nullable().optional(),
  instance_id: z.number().nullable().optional(),
  driver_node_type: z.string().nullable().optional(),
  worker_node_type: z.string().nullable().optional(),
  worker_count: z.number().nullable().optional(),
  min_autoscale_workers: z.number().nullable().optional(),
  max_autoscale_workers: z.number().nullable().optional(),
  cluster_source: z.string().nullable().optional(),
  // Derived compute classification ('all_purpose' | 'jobs' | 'dlt' |
  // 'other'). The Auto Tune tab renders only when this is 'all_purpose'.
  compute_type: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  last_state_change: z.string().nullable().optional(),
  creator_user_name: z.string().nullable().optional(),
  spark_version: z.string().nullable().optional(),
  runtime_engine: z.string().nullable().optional(),
  enable_photon: z.boolean().nullable().optional(),
  autotermination_minutes: z.number().nullable().optional(),
});

export type ClusterDetail = z.infer<typeof ClusterDetailSchema>;

// ============================================================================
// CLUSTER UTILIZATION SCHEMAS
// ============================================================================

export const ClusterUtilizationSchema = z.object({
  avg_cpu: z.number().default(0),
  max_cpu: z.number().default(0),
  min_cpu: z.number().default(0),
  avg_memory: z.number().default(0),
  max_memory: z.number().default(0),
  min_memory: z.number().default(0),
  sample_count: z.number().default(0),
});

export const ClusterUtilizationPointSchema = z.object({
  period: z.string(),
  avg_cpu: z.number().default(0),
  avg_memory: z.number().default(0),
  sample_count: z.number().default(0),
});

export const ClusterUtilizationResponseSchema = z.object({
  cluster_id: z.string(),
  utilization: ClusterUtilizationSchema,
  timeseries: z.array(ClusterUtilizationPointSchema).default([]),
});

export type ClusterUtilization = z.infer<typeof ClusterUtilizationSchema>;
export type ClusterUtilizationPoint = z.infer<typeof ClusterUtilizationPointSchema>;
export type ClusterUtilizationResponse = z.infer<typeof ClusterUtilizationResponseSchema>;

// ============================================================================
// CLUSTER COST SCHEMAS
// ============================================================================

export const ClusterCostPointSchema = z.object({
  period: z.string(),
  total_dbus: z.number().default(0),
  estimated_cost: z.number().default(0),
});

export const ClusterCostResponseSchema = z.object({
  cluster_id: z.string(),
  total_dbus: z.number().default(0),
  estimated_cost: z.number().default(0),
  daily_average_dbus: z.number().default(0),
  daily_average_cost: z.number().default(0),
  // Days in the averaged window the daily averages were computed over.
  // Not rendered anywhere yet — declared so responses parse without
  // unknown-field passthrough warnings.
  window_days: z.number().nullable().optional(),
  timeseries: z.array(ClusterCostPointSchema).default([]),
});

export type ClusterCostPoint = z.infer<typeof ClusterCostPointSchema>;
export type ClusterCostResponse = z.infer<typeof ClusterCostResponseSchema>;

// ============================================================================
// CLUSTER COST TRENDS SCHEMAS (All clusters)
// ============================================================================

export const ClusterCostTrendItemSchema = z.object({
  period: z.string(),
  compute_type: z.string(),
  total_dbus: z.number().default(0),
  estimated_cost: z.number().default(0),
});

export const ClustersCostTrendsResponseSchema = z.object({
  trends: z.array(ClusterCostTrendItemSchema),
  compute_types: z.array(z.string()),
  granularity: z.string(),
  total_dbus: z.number().default(0),
  total_cost: z.number().default(0),
});

export type ClusterCostTrendItem = z.infer<typeof ClusterCostTrendItemSchema>;
export type ClustersCostTrendsResponse = z.infer<typeof ClustersCostTrendsResponseSchema>;

// ============================================================================
// CLUSTER FILTER OPTIONS
// ============================================================================

export const ClusterFilterOptionsSchema = z.object({
  cluster_names: z.array(z.string()).default([]),
  workspaces: z.array(z.string()).default([]),
  sources: z.array(z.string()).default([]),
  driver_nodes: z.array(z.string()).default([]),
  worker_nodes: z.array(z.string()).default([]),
});

export type ClusterFilterOptions = z.infer<typeof ClusterFilterOptionsSchema>;

// ============================================================================
// PARAMS TYPES
// ============================================================================

export type ComputeType = "all_purpose" | "jobs" | "dlt";

export interface ClustersParams {
  days?: number;
  start_date?: string;
  end_date?: string;
  workspace_id?: string;
  state?: string;
  search?: string;
  limit?: number;
  offset?: number;
  page?: number;
  page_size?: number;
  cluster_names?: string[];
  workspaces?: string[];
  compute_type?: string[];
  enable_photon?: boolean;
  sortAttribute?: string;
  sortOrder?: string;
}

export interface ClusterDetailParams {
  cluster_id: string;
}

export interface ClusterTimeseriesParams {
  instance_id?: number | string;
  workspace_id?: string;
  cluster_id: string;
  start_date: string;
  end_date: string;
  granularity?: "day" | "week" | "month";
}

export interface ClustersCostTrendsParams {
  start_date: string;
  end_date: string;
  granularity?: "day" | "week" | "month";
  workspace_id?: string;
}
